import React, {useEffect, useMemo, useRef, useState} from 'react';
import {StyleSheet, css} from 'aphrodite';
import {useLocation, useNavigate} from 'react-router-dom';
import {FiArrowLeft, FiCoffee, FiFileText, FiMapPin, FiShoppingBag} from 'react-icons/fi';

import {useCart} from '../context/CartContext';
import {useDeliveryMode} from '../context/DeliveryModeContext';
import PaymentMethodSelector from '../components/cart/PaymentMethodSelector';
import {getWallet, placeOrder} from '../api/api';

const PLATFORM_FEE = 0.05;
const VAT_RATE = 0;
const BASE_DELIVERY_FEE = 8;
const FREE_DELIVERY_MINIMUM = 250;
const MAX_DELIVERY_DISTANCE_KM = 15;
const EXTRA_PER_KM_AFTER_3KM = 3.5;
const MINIMUM_CARD_PAYMENT = 1;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-places-script';
const WEB_APP_BASE_URL =
  process.env.REACT_APP_WEB_APP_URL || 'https://app.yabathoplatform.co.za';

const normalizePrice = price => {
  if (price === null || price === undefined || price === '') return 0;

  const cleaned =
    typeof price === 'string' ? price.replace(',', '.').trim() : price;

  const numericPrice = Number(cleaned);

  if (Number.isNaN(numericPrice)) return 0;

  if (Number.isInteger(numericPrice) && numericPrice >= 1000) {
    return numericPrice / 100;
  }

  return numericPrice;
};

const roundMoney = value => {
  return Number((Number(value) || 0).toFixed(2));
};

const formatMoney = value => {
  return roundMoney(value).toFixed(2);
};

const getDistanceKm = (lat1, lon1, lat2, lon2) => {
  const toRad = value => (value * Math.PI) / 180;
  const earthRadiusKm = 6371;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return earthRadiusKm * c;
};

const getBackendOrderType = currentMode => {
  const value = String(currentMode || '').trim().toLowerCase();

  if (value === 'delivery') return 'delivery';

  if (value === 'pick-up' || value === 'pickup' || value === 'pick up') {
    return 'pickup';
  }

  if (value === 'dining' || value === 'dine in' || value === 'dine-in') {
    return 'pickup';
  }

  return 'delivery';
};

const isDineInMode = currentMode => {
  const value = String(currentMode || '').trim().toLowerCase();
  return value === 'dining' || value === 'dine in' || value === 'dine-in';
};

const calculateDeliveryFee = ({subtotalAmount, distanceInKm, isDelivery}) => {
  if (!isDelivery) return 0;
  if (!Number.isFinite(distanceInKm)) return 0;
  if (distanceInKm > MAX_DELIVERY_DISTANCE_KM) return 0;

  if (subtotalAmount > FREE_DELIVERY_MINIMUM && distanceInKm <= 1) {
    return 0;
  }

  if (distanceInKm <= 3) {
    return BASE_DELIVERY_FEE;
  }

  return roundMoney(
    BASE_DELIVERY_FEE + (distanceInKm - 3) * EXTRA_PER_KM_AFTER_3KM,
  );
};

const reverseGeocodeAddress = async (latitude, longitude) => {
  const fallbackAddress = `Current location (${Number(latitude).toFixed(5)}, ${Number(longitude).toFixed(5)})`;

  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`,
      {
        headers: {
          Accept: 'application/json',
        },
      },
    );

    if (!response.ok) {
      return fallbackAddress;
    }

    const data = await response.json();

    if (data?.display_name) {
      return data.display_name;
    }

    return fallbackAddress;
  } catch (error) {
    console.error('Reverse geocode failed:', error);
    return fallbackAddress;
  }
};

const getBrowserPosition = options =>
  new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });

const loadGooglePlaces = () =>
  new Promise((resolve, reject) => {
    if (window.google?.maps?.places) {
      resolve(window.google);
      return;
    }

    if (!GOOGLE_MAPS_API_KEY) {
      reject(new Error('Google Maps key is not configured.'));
      return;
    }

    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.google), {
        once: true,
      });
      existingScript.addEventListener('error', reject, {once: true});
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = reject;
    document.head.appendChild(script);
  });

export default function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const {cartItems, clearCart} = useCart();
  const {mode} = useDeliveryMode();
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const {
    tip = 0,
    scheduledDate = '',
    scheduledTime = '',
    orderMode = mode,
    diningGuests: initialDiningGuests = 2,
    reservationNotes: initialReservationNotes = '',
    depositAmount = 0,
  } = location.state || {};

  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [diningGuests, setDiningGuests] = useState(
    Math.max(1, Number(initialDiningGuests || 2)),
  );
  const [reservationNotes, setReservationNotes] = useState(
    initialReservationNotes || '',
  );
  const [paymentMethod, setPaymentMethod] = useState('Card');
  const [wallet, setWallet] = useState(null);
  const [useWallet, setUseWallet] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [addressHelper, setAddressHelper] = useState(
    GOOGLE_MAPS_API_KEY
      ? 'Start typing and choose your address from Google suggestions.'
      : 'Type your address manually, or use current location to calculate delivery distance.',
  );

  const [coords, setCoords] = useState({
    lat: null,
    lng: null,
  });

  useEffect(() => {
    getWallet().then(setWallet).catch(() => setWallet(null));
  }, []);

  const firstItem = cartItems[0] || {};
  const restaurantData =
    firstItem.restaurantData || firstItem.restaurant || firstItem.outlet || {};

  const restaurantLat = Number(
    firstItem.restaurantLatitude ??
      restaurantData?.coordinates?.latitude ??
      restaurantData?.latitude,
  );

  const restaurantLng = Number(
    firstItem.restaurantLongitude ??
      restaurantData?.coordinates?.longitude ??
      restaurantData?.longitude,
  );

  const customerLat = Number(coords.lat);
  const customerLng = Number(coords.lng);

  const hasRestaurantCoords =
    Number.isFinite(restaurantLat) && Number.isFinite(restaurantLng);

  const hasCustomerCoords =
    Number.isFinite(customerLat) &&
    Number.isFinite(customerLng) &&
    !(customerLat === 0 && customerLng === 0);

  const backendOrderType = getBackendOrderType(orderMode || mode);
  const isDeliveryMode = backendOrderType === 'delivery';

  const subtotal = useMemo(() => {
    return roundMoney(
      cartItems.reduce((sum, item) => {
        return sum + normalizePrice(item.price) * Number(item.quantity || 0);
      }, 0),
    );
  }, [cartItems]);

  const platformFee = roundMoney(subtotal * PLATFORM_FEE);
  const vatAmount = roundMoney(subtotal * VAT_RATE);

  const distanceKm = useMemo(() => {
    if (!hasRestaurantCoords || !hasCustomerCoords) return null;

    return getDistanceKm(
      restaurantLat,
      restaurantLng,
      customerLat,
      customerLng,
    );
  }, [
    hasRestaurantCoords,
    hasCustomerCoords,
    restaurantLat,
    restaurantLng,
    customerLat,
    customerLng,
  ]);

  const deliveryFee = roundMoney(
    calculateDeliveryFee({
      subtotalAmount: subtotal,
      distanceInKm: distanceKm,
      isDelivery: isDeliveryMode,
    }),
  );
  const isOutsideDeliveryRange =
    isDeliveryMode &&
    Number.isFinite(distanceKm) &&
    distanceKm > MAX_DELIVERY_DISTANCE_KM;

  const total = roundMoney(
    subtotal + deliveryFee + platformFee + vatAmount + Number(tip || 0),
  );
  const isDineInOrder = isDineInMode(orderMode);
  const requestedDineInDeposit = roundMoney(Number(depositAmount || 0));
  const dineInDeposit =
    isDineInOrder
      ? roundMoney(
          Math.min(
            total,
            requestedDineInDeposit > 0
              ? requestedDineInDeposit
              : roundMoney(total * 0.5),
          ),
        )
      : 0;
  const balanceDue =
    isDineInOrder ? roundMoney(total - dineInDeposit) : 0;
  const payNowAmount = isDineInOrder && dineInDeposit > 0 ? dineInDeposit : total;
  const walletApplied = useWallet ? Math.min(Number(wallet?.balance || 0), payNowAmount) : 0;
  const cardAmountDue = roundMoney(payNowAmount - walletApplied);

  const isCardPayment =
    paymentMethod?.toLowerCase() === 'card' ||
    paymentMethod?.toLowerCase() === 'debit card' ||
    paymentMethod?.toLowerCase() === 'credit card';

  const paymentSuccessUrl = `${WEB_APP_BASE_URL}/payment/success`;
  const paymentCancelUrl = `${WEB_APP_BASE_URL}/payment/cancel`;

  useEffect(() => {
    if (!isDeliveryMode || !addressInputRef.current || autocompleteRef.current) {
      return undefined;
    }

    let mounted = true;
    let listener = null;

    loadGooglePlaces()
      .then(google => {
        if (!mounted || !addressInputRef.current) return;

        autocompleteRef.current = new google.maps.places.Autocomplete(
          addressInputRef.current,
          {
            componentRestrictions: {country: 'za'},
            fields: ['formatted_address', 'geometry', 'name'],
            types: ['geocode', 'establishment'],
          },
        );

        listener = autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current.getPlace();
          const selectedAddress =
            place.formatted_address || place.name || addressInputRef.current.value;
          const location = place.geometry?.location;

          setAddress(selectedAddress);
          setAddressHelper('Address selected from Google suggestions.');

          if (location) {
            setCoords({
              lat: location.lat(),
              lng: location.lng(),
            });
          }
        });
      })
      .catch(() => {
        if (mounted) {
          setAddressHelper(
            'Google address suggestions are unavailable. Type your address manually, or use current location.',
          );
        }
      });

    return () => {
      mounted = false;
      if (listener) {
        listener.remove();
      }
    };
  }, [isDeliveryMode]);

  const useBrowserLocation = async () => {
    if (!navigator.geolocation) {
      setErrorMessage('Browser location is not supported on this device.');
      return;
    }

    setErrorMessage('');
    setLoadingLocation(true);

    try {
      let position;

      try {
        position = await getBrowserPosition({
          enableHighAccuracy: true,
          timeout: 12000,
          maximumAge: 0,
        });
      } catch {
        position = await getBrowserPosition({
          enableHighAccuracy: false,
          timeout: 18000,
          maximumAge: 60000,
        });
      }

      const latitude = position.coords.latitude;
      const longitude = position.coords.longitude;

      setCoords({
        lat: latitude,
        lng: longitude,
      });

      setAddress(`Current location (${latitude.toFixed(5)}, ${longitude.toFixed(5)})`);

      const resolvedAddress = await reverseGeocodeAddress(latitude, longitude);
      setAddress(resolvedAddress);
    } catch (error) {
      setErrorMessage(
        error?.code === 1
          ? 'Location permission is blocked. Please allow location access in your browser.'
          : 'Could not get your current location. Please try again or type your address.',
      );
    } finally {
      setLoadingLocation(false);
    }
  };

  const handlePlaceOrder = async () => {
    const token = localStorage.getItem('token');

    if (!token || token === 'null') {
      navigate('/');
      return;
    }

    if (!cartItems.length) {
      setErrorMessage('Your cart is empty.');
      return;
    }

    if (isDeliveryMode) {
      if (!address.trim()) {
        setErrorMessage('Enter your delivery address.');
        return;
      }

      if (!hasCustomerCoords) {
        setErrorMessage(
          'Please use current location so delivery fee can be calculated.',
        );
        return;
      }

      if (!hasRestaurantCoords) {
        setErrorMessage('Restaurant coordinates are missing for this outlet.');
        return;
      }

      if (isOutsideDeliveryRange) {
        setErrorMessage(
          `Delivery is only available within ${MAX_DELIVERY_DISTANCE_KM} km of the outlet. This address is ${distanceKm.toFixed(2)} km away.`,
        );
        return;
      }
    }

    if (!isDeliveryMode && (!scheduledDate || !scheduledTime)) {
      setErrorMessage(
        `Missing ${isDineInOrder ? 'dining' : 'pickup'} date/time.`,
      );
      return;
    }

    if (!paymentMethod) {
      setErrorMessage('Choose payment method.');
      return;
    }

    if (isCardPayment && cardAmountDue > 0 && cardAmountDue < MINIMUM_CARD_PAYMENT) {
      setErrorMessage(
        `The remaining card amount is R ${formatMoney(cardAmountDue)}. Card payments require at least R 1.00. Use less wallet balance or add another item.`,
      );
      return;
    }

    try {
      setLoadingOrder(true);
      setErrorMessage('');
      setSuccessMessage('');

      const orderData = {
        source: 'web',
        platform: 'web',
        deliveryMode: backendOrderType,
        restaurantId: cartItems[0]?.restaurantId,
        notes,
        paymentMethod: isCardPayment ? 'card' : paymentMethod,
        useWallet,
        walletAmount: walletApplied,
        subtotal,
        totalPrice: total,
        deliveryFee,
        platformFee,
        vat: vatAmount,
        tipAmount: Number(tip || 0),
        // Mobile sends successUrl/cancelUrl. On web these must be normal browser URLs.
        successUrl: paymentSuccessUrl,
        failUrl: paymentCancelUrl,
        cancelUrl: paymentCancelUrl,
        failureUrl: paymentCancelUrl,
        // Kept as aliases in case the backend also supports explicit web redirect names.
        success_url: paymentSuccessUrl,
        fail_url: paymentCancelUrl,
        cancel_url: paymentCancelUrl,
        failure_url: paymentCancelUrl,
        returnUrl: paymentSuccessUrl,
        return_url: paymentSuccessUrl,
        redirectUrl: paymentSuccessUrl,
        redirect_url: paymentSuccessUrl,
        successRedirectUrl: paymentSuccessUrl,
        cancelRedirectUrl: paymentCancelUrl,
        failureRedirectUrl: paymentCancelUrl,
        items: cartItems.map(item => ({
          menuItemId: item.menuItemId || item.id,
          quantity: Number(item.quantity || 1),
        })),
      };

      if (isDeliveryMode) {
        orderData.deliveryAddress = address;
        orderData.latitude = customerLat;
        orderData.longitude = customerLng;
        orderData.distanceKm = Number.isFinite(distanceKm)
          ? Number(distanceKm.toFixed(2))
          : null;
      } else {
        orderData.scheduledDate = scheduledDate;
        orderData.scheduledTime = scheduledTime;
        orderData.orderMode = orderMode;
        if (isDineInOrder) {
          orderData.diningGuests = Number(diningGuests || 1);
          orderData.reservationNotes = reservationNotes;
          orderData.depositAmount = dineInDeposit;
          orderData.balanceDue = balanceDue;
        }
      }

      const response = await placeOrder(orderData);

      const order = response?.data?.order || response?.order || response?.data || response;
      const payment = response?.data?.payment || response?.payment;

      const localOrder = {
        id: order?.id || order?._id || `local_${Date.now()}`,
        status: order?.status || 'pending',
        deliveryMode: backendOrderType,
        orderMode,
        scheduledDate,
        scheduledTime,
        diningGuests,
        reservationNotes,
        deliveryAddress: address,
        restaurantName:
          cartItems[0]?.restaurantName ||
          cartItems[0]?.restaurant ||
          restaurantData?.name ||
          'Restaurant',
        totalPrice: total,
        amount: payNowAmount,
        paymentAmount: payNowAmount,
        payment_amount: payNowAmount,
        payNowAmount,
        paylinkAmount: payNowAmount,
        amountToPay: payNowAmount,
        deliveryFee,
        platformFee,
        vat: vatAmount,
        tipAmount: Number(tip || 0),
        depositAmount: dineInDeposit,
        balanceDue,
        orderItems: cartItems,
        createdAt: new Date().toISOString(),
      };

      const pendingPaymentData = {
        order: localOrder,
        serverOrder: order,
        payment,
        createdAt: new Date().toISOString(),
      };

      localStorage.setItem('pendingPaymentData', JSON.stringify(pendingPaymentData));

      const paymentUrl =
        payment?.paylinkUrl ||
        payment?.payLinkUrl ||
        payment?.paymentLink ||
        payment?.payment_link ||
        payment?.authorization_url ||
        payment?.authorizationUrl ||
        payment?.paymentUrl ||
        payment?.url ||
        order?.paylinkUrl ||
        order?.paymentLink ||
        order?.paymentUrl;

      if (isCardPayment && Number(payment?.amount || 0) > 0) {
        if (!paymentUrl) {
          throw new Error(
            'Order was created, but iKhokha did not return a payment link. Please check the backend /orders response for payment.paylinkUrl.',
          );
        }

        window.location.assign(paymentUrl);
        return;
      }

      clearCart();
      localStorage.removeItem('pendingPaymentData');
      setSuccessMessage('Order placed successfully.');

      setTimeout(() => {
        navigate('/orders');
      }, 900);
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to place order.');
    } finally {
      setLoadingOrder(false);
    }
  };

  const SummaryRow = ({label, amount}) => (
    <div className={css(styles.summaryRow)}>
      <span>{label}</span>
      <strong>R {formatMoney(amount)}</strong>
    </div>
  );

  return (
    <div className={css(styles.page)}>
      <main className={css(styles.shell)}>
        <button
          type="button"
          className={css(styles.backButton)}
          onClick={() => navigate('/cart')}>
          <FiArrowLeft /> Back to cart
        </button>

        <section className={css(styles.header)}>
          <div>
            <p className={css(styles.kicker)}>YaBatho Checkout</p>
            <h1 className={css(styles.title)}>Complete your order</h1>
            <p className={css(styles.subtitle)}>
              Confirm delivery details, payment method, and final amount.
            </p>
          </div>

          <div className={css(styles.headerBadge)}><FiFileText /></div>
        </section>

        <section className={css(styles.grid)}>
          <div className={css(styles.leftColumn)}>
            {isDeliveryMode ? (
              <div className={css(styles.card)}>
                <div className={css(styles.sectionHeader)}>
                  <div className={css(styles.sectionIcon)}><FiMapPin /></div>
                  <div>
                    <p className={css(styles.sectionKicker)}>
                      Delivery address
                    </p>
                    <h2 className={css(styles.sectionTitle)}>
                      Where should we deliver?
                    </h2>
                    <p className={css(styles.sectionHelper)}>
                      Your browser location is used to calculate delivery
                      distance and fee.
                    </p>
                  </div>
                </div>

                <label className={css(styles.label)}>Delivery Address *</label>
                <input
                  ref={addressInputRef}
                  className={css(styles.input)}
                  value={address}
                  onChange={event => {
                    setAddress(event.target.value);
                    if (!event.target.value.trim()) {
                      setCoords({lat: null, lng: null});
                    }
                  }}
                  placeholder="Start typing your delivery address"
                />

                <p className={css(styles.addressHelper)}>{addressHelper}</p>

                <button
                  type="button"
                  className={css(
                    styles.locationButton,
                    loadingLocation && styles.disabledButton,
                  )}
                  onClick={useBrowserLocation}
                  disabled={loadingLocation}>
                  {loadingLocation ? 'Getting address...' : 'Use current location'}
                </button>

                {hasCustomerCoords ? (
                  <p className={css(styles.locationText)}>
                    Location selected: {customerLat.toFixed(5)},{' '}
                    {customerLng.toFixed(5)}
                  </p>
                ) : null}
              </div>
            ) : (
              <div className={css(styles.card)}>
                <div className={css(styles.sectionHeader)}>
                  <div className={css(styles.sectionIcon)}>
                    {isDineInOrder ? <FiCoffee /> : <FiShoppingBag />}
                  </div>
                  <div>
                    <p className={css(styles.sectionKicker)}>
                      {orderMode} details
                    </p>
                    <h2 className={css(styles.sectionTitle)}>
                      {isDineInOrder
                        ? 'Dining schedule'
                        : 'Pickup schedule'}
                    </h2>
                    <p className={css(styles.sectionHelper)}>
                      No delivery fee will be charged for this order.
                    </p>
                  </div>
                </div>

                <div className={css(styles.scheduleBox)}>
                  <div className={css(styles.scheduleValueCard)}>
                    <span>Date </span>
                    <strong>{scheduledDate || 'Not selected'}</strong>
                  </div>

                  <div className={css(styles.scheduleValueCard)}>
                    <span>Time </span>
                    <strong>{scheduledTime || 'Not selected'}</strong>
                  </div>
                </div>

                {isDineInOrder ? (
                  <div className={css(styles.dineInDetailsBox)}>
                    <label className={css(styles.label)}>Number of guests</label>
                    <div className={css(styles.guestStepper)}>
                      <button
                        type="button"
                        onClick={() =>
                          setDiningGuests(current => Math.max(1, current - 1))
                        }>
                        -
                      </button>
                      <strong>
                        {diningGuests}{' '}
                        {diningGuests === 1 ? 'guest' : 'guests'}
                      </strong>
                      <button
                        type="button"
                        onClick={() =>
                          setDiningGuests(current => Math.min(30, current + 1))
                        }>
                        +
                      </button>
                    </div>

                    <label className={css(styles.label)}>
                      Reservation notes
                    </label>
                    <textarea
                      className={css(styles.reservationTextArea)}
                      value={reservationNotes}
                      onChange={event => setReservationNotes(event.target.value)}
                      placeholder="Table preference, celebration, allergies..."
                    />
                  </div>
                ) : null}
              </div>
            )}

            <div className={css(styles.card)}>
              <div className={css(styles.sectionHeader)}>
                <div className={css(styles.sectionIcon)}>📝</div>
                <div>
                  <p className={css(styles.sectionKicker)}>Order notes</p>
                  <h2 className={css(styles.sectionTitle)}>
                    Special instructions
                  </h2>
                  <p className={css(styles.sectionHelper)}>
                    Add special instructions for the outlet or rider.
                  </p>
                </div>
              </div>

              <textarea
                className={css(styles.textArea)}
                value={notes}
                onChange={event => setNotes(event.target.value)}
                placeholder="E.g. Leave at gate, no pickles..."
              />
            </div>

            <div className={css(styles.card)}>
              <div className={css(styles.walletRow)}>
                <div><p className={css(styles.sectionKicker)}>YABATHO WALLET</p><h2 className={css(styles.sectionTitle)}>R {formatMoney(wallet?.balance || 0)} available</h2><p className={css(styles.sectionHelper)}>{useWallet ? `R ${formatMoney(walletApplied)} applied${cardAmountDue > 0 ? ` · R ${formatMoney(cardAmountDue)} by card` : ' · paid fully by wallet'}` : 'Use your balance toward this order.'}</p></div>
                <button type="button" className={css(styles.walletButton, useWallet && styles.walletButtonActive)} onClick={() => setUseWallet(value => !value)}>{useWallet ? 'Applied' : 'Use wallet'}</button>
              </div>
            </div>

            <div className={css(styles.card)}>
              <PaymentMethodSelector
                selectedMethod={paymentMethod}
                onSelect={setPaymentMethod}
              />
            </div>
          </div>

          <aside className={css(styles.summaryCard)}>
            <p className={css(styles.sectionKicker)}>Review</p>
            <h2 className={css(styles.sectionTitle)}>Order summary</h2>

            <div className={css(styles.orderItems)}>
              {cartItems.map(item => (
                <div key={item.id} className={css(styles.orderItem)}>
                  <span>
                    {item.quantity} × {item.name}
                  </span>
                  <strong>
                    R{' '}
                    {formatMoney(
                      normalizePrice(item.price) * Number(item.quantity || 0),
                    )}
                  </strong>
                </div>
              ))}
            </div>

            <SummaryRow label="Subtotal" amount={subtotal} />

            {isDeliveryMode ? (
              <div className={css(styles.summaryTextRow)}>
                <span>Distance</span>
                <strong>
                  {Number.isFinite(distanceKm) ? distanceKm.toFixed(2) : '0.00'} km
                </strong>
              </div>
            ) : null}

            {isDeliveryMode ? (
              <SummaryRow label="Delivery Fee" amount={deliveryFee} />
            ) : null}

            {isOutsideDeliveryRange ? (
              <p className={css(styles.errorBox)}>
                Delivery is only available within {MAX_DELIVERY_DISTANCE_KM} km
                of the outlet. Please choose a closer address or use pick-up.
              </p>
            ) : null}

            {!isDeliveryMode && scheduledDate ? (
              <div className={css(styles.summaryTextRow)}>
                <span>
                  {isDineInOrder ? 'Dining Date' : 'Pickup Date'}
                </span>
                <strong>{scheduledDate}</strong>
              </div>
            ) : null}

            {!isDeliveryMode && scheduledTime ? (
              <div className={css(styles.summaryTextRow)}>
                <span>
                  {isDineInOrder ? 'Arrival Time' : 'Pickup Time'}
                </span>
                <strong>{scheduledTime}</strong>
              </div>
            ) : null}

            {!isDeliveryMode && isDineInOrder ? (
              <>
                <div className={css(styles.summaryTextRow)}>
                  <span>Guests</span>
                  <strong>
                    {Number(diningGuests || 1)}{' '}
                    {Number(diningGuests || 1) === 1 ? 'guest' : 'guests'}
                  </strong>
                </div>
                {reservationNotes ? (
                  <div className={css(styles.summaryTextRow)}>
                    <span>Reservation notes</span>
                    <strong>{reservationNotes}</strong>
                  </div>
                ) : null}
              </>
            ) : null}

            <SummaryRow label="Platform Fee (5%)" amount={platformFee} />
            <SummaryRow label="VAT included" amount={vatAmount} />

            {Number(tip) > 0 ? (
              <SummaryRow label="Tip" amount={Number(tip)} />
            ) : null}

            <div className={css(styles.totalRow)}>
              <div className={css(styles.totalLabelStack)}>
                <span>Total </span>
                <small>
                  {isDineInOrder ? 'Dine-in order total' : 'Order total'}
                </small>
              </div>
              <strong>R {formatMoney(total)}</strong>
            </div>

            {isDineInOrder && dineInDeposit > 0 ? (
              <div className={css(styles.depositSummary)}>
                <div>
                  <span>Deposit due now </span>
                  <strong>R{formatMoney(dineInDeposit)}</strong>
                </div>
                <div>
                  <span>Balance at restaurant </span>
                  <strong>R{formatMoney(balanceDue)}</strong>
                </div>
              </div>
            ) : null}

            {isDineInOrder && dineInDeposit > 0 ? (
              <p className={css(styles.payNowNotice)}>
                You will pay R{formatMoney(payNowAmount)} now. The remaining R
                {formatMoney(balanceDue)} is paid at the restaurant.
              </p>
            ) : null}

            {errorMessage ? (
              <p className={css(styles.errorBox)}>{errorMessage}</p>
            ) : null}

            {successMessage ? (
              <p className={css(styles.successBox)}>{successMessage}</p>
            ) : null}

            <button
              type="button"
              className={css(
                styles.placeOrderButton,
                loadingOrder && styles.disabledButton,
              )}
              onClick={handlePlaceOrder}
              disabled={loadingOrder}>
              {loadingOrder
                ? 'Placing order...'
                : isCardPayment
                ? 'Proceed to Payment'
                : 'Place Order'}
            </button>
          </aside>
        </section>
      </main>
    </div>
  );
}

const styles = StyleSheet.create({
  page: {
    minHeight: '100vh',
    backgroundColor: '#FFFDF7',
    color: '#0F3D2E',
    fontFamily: 'Inter, Segoe UI, Arial, sans-serif',
    padding: '24px 24px 112px',
    boxSizing: 'border-box',

    '@media (max-width: 720px)': {
      padding: '16px 14px 104px',
    },
  },

  shell: {
    width: '100%',
    maxWidth: '1120px',
    margin: '0 auto',
  },

  backButton: {
    border: '1px solid #E6D8BE',
    backgroundColor: '#ffffff',
    color: '#0F3D2E',
    borderRadius: '999px',
    padding: '10px 14px',
    fontWeight: '900',
    cursor: 'pointer',
    marginBottom: '18px',
  },

  header: {
    backgroundColor: '#0F3D2E',
    border: '1px solid rgba(201, 161, 74, 0.45)',
    borderRadius: '34px',
    padding: '30px',
    boxShadow: '0 22px 50px rgba(15, 61, 46, 0.16)',
    color: '#ffffff',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '24px',
    marginBottom: '22px',

    '@media (max-width: 720px)': {
      borderRadius: '28px',
      padding: '22px',
      flexDirection: 'column',
    },
  },

  kicker: {
    margin: '0 0 8px',
    color: '#C9A14A',
    fontSize: '12px',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },

  title: {
    margin: 0,
    color: '#ffffff',
    fontSize: '42px',
    lineHeight: 1.04,
    letterSpacing: '-1.8px',
    fontWeight: '900',

    '@media (max-width: 720px)': {
      fontSize: '32px',
      letterSpacing: '-1.2px',
    },
  },

  subtitle: {
    maxWidth: '680px',
    margin: '14px 0 0',
    color: '#F4EBD8',
    fontSize: '15px',
    lineHeight: 1.75,
  },

  headerBadge: {
    width: '64px',
    height: '64px',
    minWidth: '64px',
    borderRadius: '50%',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },

  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 390px',
    gap: '18px',
    alignItems: 'flex-start',

    '@media (max-width: 960px)': {
      gridTemplateColumns: '1fr',
    },
  },

  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },

  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #E6D8BE',
    borderRadius: '30px',
    padding: '18px',
    boxShadow: '0 12px 34px rgba(30, 30, 30, 0.06)',
  },

  sectionHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '16px',
  },

  sectionIcon: {
    width: '46px',
    height: '46px',
    minWidth: '46px',
    borderRadius: '50%',
    backgroundColor: '#0F3D2E',
    color: '#C9A14A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  sectionKicker: {
    margin: '0 0 5px',
    color: '#C9A14A',
    fontSize: '11px',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '0.9px',
  },

  sectionTitle: {
    margin: 0,
    color: '#0F3D2E',
    fontSize: '22px',
    fontWeight: '900',
  },

  sectionHelper: {
    margin: '6px 0 0',
    color: '#6E6254',
    fontSize: '13px',
    lineHeight: 1.6,
  },

  label: {
    display: 'block',
    color: '#0F3D2E',
    fontSize: '13px',
    fontWeight: '900',
    marginBottom: '8px',
  },

  input: {
    width: '100%',
    border: '1px solid #E6D8BE',
    backgroundColor: '#FFFDF7',
    color: '#0F3D2E',
    borderRadius: '18px',
    padding: '14px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: '12px',
    lineHeight: 1.5,
  },

  locationButton: {
    border: 'none',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    borderRadius: '18px',
    padding: '13px 15px',
    fontSize: '14px',
    fontWeight: '900',
    cursor: 'pointer',
  },

  addressHelper: {
    margin: '-4px 0 12px',
    color: '#6E6254',
    fontSize: '12px',
    lineHeight: 1.45,
    fontWeight: '700',
  },

  locationText: {
    margin: '12px 0 0',
    color: '#0F3D2E',
    backgroundColor: '#F4EBD8',
    border: '1px solid #E6D8BE',
    borderRadius: '16px',
    padding: '10px 12px',
    fontSize: '12px',
    fontWeight: '800',
  },

  scheduleBox: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '14px',

    '@media (max-width: 520px)': {
      gridTemplateColumns: '1fr',
    },

    div: {
      backgroundColor: '#FFFDF7',
      border: '1px solid #F1E7D2',
      borderRadius: '20px',
      padding: '15px',
      minWidth: 0,
      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.8)',
    },

    span: {
      display: 'block',
      color: '#8C8170',
      fontSize: '12px',
      marginBottom: '6px',
      fontWeight: '800',
    },

    strong: {
      display: 'block',
      color: '#0F3D2E',
      fontSize: '16px',
      fontWeight: '900',
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
    },
  },

  scheduleValueCard: {
    minWidth: 0,
  },

  dineInDetailsBox: {
    marginTop: '14px',
    border: '1px solid #F1E7D2',
    backgroundColor: '#FFFDF7',
    borderRadius: '22px',
    padding: '14px',
  },

  guestStepper: {
    display: 'grid',
    gridTemplateColumns: '48px 1fr 48px',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: '#F4EBD8',
    border: '1px solid #E6D8BE',
    borderRadius: '18px',
    padding: '8px',
    marginBottom: '14px',

    button: {
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      border: 'none',
      backgroundColor: '#C9A14A',
      color: '#0F3D2E',
      fontSize: '22px',
      fontWeight: '900',
      cursor: 'pointer',
    },

    strong: {
      textAlign: 'center',
      color: '#0F3D2E',
      fontSize: '16px',
      fontWeight: '900',
    },
  },

  textArea: {
    width: '100%',
    minHeight: '110px',
    resize: 'vertical',
    border: '1px solid #E6D8BE',
    backgroundColor: '#FFFDF7',
    color: '#0F3D2E',
    borderRadius: '18px',
    padding: '14px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'Inter, Segoe UI, Arial, sans-serif',
  },

  reservationTextArea: {
    width: '100%',
    minHeight: '92px',
    resize: 'vertical',
    border: '1px solid #E6D8BE',
    backgroundColor: '#ffffff',
    color: '#0F3D2E',
    borderRadius: '18px',
    padding: '14px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'Inter, Segoe UI, Arial, sans-serif',
  },

  summaryCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #E6D8BE',
    borderRadius: '30px',
    padding: '18px',
    boxShadow: '0 12px 34px rgba(30, 30, 30, 0.06)',
    position: 'sticky',
    top: '20px',

    '@media (max-width: 960px)': {
      position: 'relative',
      top: 'auto',
    },
  },

  orderItems: {
    marginTop: '15px',
    marginBottom: '12px',
    backgroundColor: '#FFFDF7',
    border: '1px solid #F1E7D2',
    borderRadius: '20px',
    padding: '10px',
  },

  orderItem: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '9px 0',
    borderBottom: '1px solid #F1E7D2',

    ':last-child': {
      borderBottom: 'none',
    },

    span: {
      color: '#0F3D2E',
      fontSize: '13px',
      fontWeight: '800',
    },

    strong: {
      color: '#C9A14A',
      fontSize: '13px',
    },
  },

  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '12px 0',
    borderBottom: '1px solid #F1E7D2',
    alignItems: 'flex-start',

    span: {
      color: '#6E6254',
      fontSize: '14px',
      lineHeight: 1.45,
    },

    strong: {
      color: '#0F3D2E',
      fontSize: '14px',
      textAlign: 'right',
      whiteSpace: 'nowrap',
    },
  },

  summaryTextRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '12px 0',
    borderBottom: '1px solid #F1E7D2',
    alignItems: 'flex-start',

    span: {
      color: '#6E6254',
      fontSize: '14px',
      lineHeight: 1.45,
    },

    strong: {
      color: '#0F3D2E',
      fontSize: '14px',
      textAlign: 'right',
      overflowWrap: 'anywhere',
      minWidth: 0,
    },
  },

  totalRow: {
    marginTop: '14px',
    backgroundColor: '#F4EBD8',
    border: '1px solid #E6D8BE',
    borderRadius: '22px',
    padding: '15px',
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    alignItems: 'center',

    '@media (max-width: 420px)': {
      alignItems: 'flex-start',
      flexDirection: 'column',
    },

    span: {
      color: '#0F3D2E',
      fontSize: '15px',
      fontWeight: '900',
    },

    small: {
      display: 'block',
      color: '#6E6254',
      fontSize: '12px',
      fontWeight: '800',
      marginTop: '4px',
      lineHeight: 1.35,
    },

    strong: {
      color: '#0F3D2E',
      fontSize: '24px',
      fontWeight: '900',
      whiteSpace: 'nowrap',
    },
  },

  totalLabelStack: {
    minWidth: 0,
  },

  depositSummary: {
    marginTop: '12px',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
    gap: '10px',

    div: {
      border: '1px solid #E6D8BE',
      borderRadius: '18px',
      padding: '13px',
      backgroundColor: '#FFFDF7',
    },

    span: {
      display: 'block',
      color: '#746A5B',
      fontSize: '12px',
      fontWeight: '900',
      textTransform: 'uppercase',
    },

    strong: {
      display: 'block',
      color: '#0F3D2E',
      fontSize: '20px',
      fontWeight: '900',
      marginTop: '4px',
    },
  },

  payNowNotice: {
    margin: '12px 0 0',
    backgroundColor: '#F7F0E3',
    border: '1px solid #E6D8BE',
    color: '#0F3D2E',
    borderRadius: '16px',
    padding: '12px 13px',
    fontSize: '13px',
    fontWeight: '800',
    lineHeight: 1.5,
  },

  walletRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '18px',
    '@media (max-width: 560px)': {alignItems: 'stretch', flexDirection: 'column'},
  },

  walletButton: {
    border: '1px solid #E6D8BE',
    backgroundColor: '#F4EBD8',
    color: '#0F3D2E',
    borderRadius: '16px',
    padding: '12px 18px',
    fontWeight: '900',
    cursor: 'pointer',
  },

  walletButtonActive: {
    backgroundColor: '#0F3D2E',
    borderColor: '#0F3D2E',
    color: '#FFFFFF',
  },

  errorBox: {
    margin: '14px 0 0',
    backgroundColor: '#FFE9E9',
    border: '1px solid #F5B8B8',
    color: '#D9534F',
    borderRadius: '16px',
    padding: '11px 12px',
    fontSize: '13px',
    fontWeight: '800',
  },

  successBox: {
    margin: '14px 0 0',
    backgroundColor: '#E9F7EF',
    border: '1px solid #BFE7CE',
    color: '#0F3D2E',
    borderRadius: '16px',
    padding: '11px 12px',
    fontSize: '13px',
    fontWeight: '800',
  },

  placeOrderButton: {
    width: '100%',
    border: 'none',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    borderRadius: '20px',
    padding: '15px',
    marginTop: '16px',
    fontSize: '15px',
    fontWeight: '900',
    cursor: 'pointer',
  },

  disabledButton: {
    opacity: 0.65,
    cursor: 'not-allowed',
  },
});
